import React, { useState, useEffect } from 'react';
import { TouristSite, TourQuestionnaire, Settings } from '../../../types';
import { ChevronUp, ChevronDown, Clock, Star, Info, Accessibility, Route } from 'lucide-react';
import { cn } from '../../../lib/utils';
import { simulateDayDuration } from '../../../services/circuitEngine';

interface Step9_5Props {
  sitesToRank: TouristSite[];
  orderedIds: string[];
  setOrderedIds: React.Dispatch<React.SetStateAction<string[]>>;
  isHeavy: boolean;
  questionnaire: TourQuestionnaire;
  settings: Settings;
  driverHomeLocation: { lat: number; lng: number } | null;
  idealDays: number;
  handleNext: () => void;
  handleBack: () => void;
  setSelectedActivity: (site: TouristSite | null) => void;
  setPreviousStep: (step: number) => void;
  setStep: (step: number) => void;
}

export const Step9_5: React.FC<Step9_5Props> = ({
  sitesToRank,
  orderedIds,
  setOrderedIds,
  isHeavy,
  questionnaire,
  settings,
  driverHomeLocation,
  idealDays,
  handleNext,
  handleBack,
  setSelectedActivity,
  setPreviousStep,
  setStep,
}) => {
  // Initialize orderedIds with all sites if empty
  useEffect(() => {
    if (orderedIds.length === 0) {
      setOrderedIds(sitesToRank.map(s => s.id));
    }
  }, []);

  // Build ordered list: orderedIds first, then any unordered sites
  const orderedSites: TouristSite[] = orderedIds
    .map(id => sitesToRank.find(s => s.id === id))
    .filter(Boolean) as TouristSite[];
  const unorderedSites = sitesToRank.filter(s => !orderedIds.includes(s.id));
  const allSites = [...orderedSites, ...unorderedSites];

  // How many sites fit in the chosen number of days
  const [fittedCount, setFittedCount] = useState<number>(sitesToRank.length);

  useEffect(() => {
    if (!questionnaire.pickupLocation) {
      setFittedCount(sitesToRank.length);
      return;
    }

    const numberOfDays = questionnaire.logistics?.days || 1;
    const maxDuration = (settings.tourMaxDurationFullDay || 10) * 60 + 30;
    const pickupLoc = questionnaire.pickupLocation;

    let remaining = [...allSites];
    let totalFitted = 0;

    for (let day = 0; day < numberOfDays; day++) {
      const dayNum = day + 1;
      const startLoc = day === 0 ? pickupLoc : (questionnaire.accommodationCoords || pickupLoc);
      const endLoc = (day === numberOfDays - 1 || questionnaire.accommodationType === 'return')
        ? pickupLoc : (questionnaire.accommodationCoords || pickupLoc);
      const startId = day === 0 ? 'PICKUP' : 'ACCOMMODATION';
      const endId = (day === numberOfDays - 1 || questionnaire.accommodationType === 'return') ? 'PICKUP' : 'ACCOMMODATION';
      const dayDepartureTime = questionnaire.departureTimes?.[dayNum] || questionnaire.departureTime;

      let fittedToday: TouristSite[] = [];
      let i = 0;
      while (i < remaining.length) {
        const testSites = [...fittedToday, remaining[i]];
        const { duration } = simulateDayDuration(
          testSites, startLoc, endLoc, startId, endId,
          dayDepartureTime, questionnaire, settings, dayNum, [], undefined
        );
        if (duration <= maxDuration) {
          fittedToday = testSites;
          remaining.splice(i, 1);
        } else {
          i++;
        }
      }
      totalFitted += fittedToday.length;
    }

    setFittedCount(Math.max(1, totalFitted));
  }, [orderedIds, questionnaire, settings]);

  const moveUp = (index: number) => {
    if (index === 0) return;
    const newOrder = allSites.map(s => s.id);
    [newOrder[index - 1], newOrder[index]] = [newOrder[index], newOrder[index - 1]];
    setOrderedIds(newOrder);
  };

  const moveDown = (index: number) => {
    if (index === allSites.length - 1) return;
    const newOrder = allSites.map(s => s.id);
    [newOrder[index], newOrder[index + 1]] = [newOrder[index + 1], newOrder[index]];
    setOrderedIds(newOrder);
  };

  const openDetail = (site: TouristSite) => {
    setSelectedActivity(site);
    setPreviousStep(9.5);
    setStep(10);
  };

  const excludedCount = allSites.length - fittedCount;

  return (
    <div className="flex flex-col h-[100dvh] p-0 overflow-hidden bg-zinc-950">
      <div className="flex-1 overflow-y-auto p-6 space-y-4 custom-scrollbar pb-32">
        <h2 className="text-2xl font-bold text-white">Ordre de préférence</h2>

        <p className="text-zinc-400 text-sm">
          {excludedCount > 0
            ? `Votre sélection dépasse le temps disponible sur ${questionnaire.logistics?.days || 1} jour${(questionnaire.logistics?.days || 1) > 1 ? 's' : ''}. Les ${excludedCount} activité${excludedCount > 1 ? 's' : ''} en bas de liste seront retirées automatiquement.`
            : `Classez vos activités par ordre de préférence. Toutes rentrent dans votre programme.`
          }
          <span className="block mt-1 text-emerald-400 font-semibold text-xs">
            L'ordre de visite réel sera optimisé par notre algorithme pour limiter les temps de trajet.
          </span>
        </p>

        <div className="space-y-2">
          {allSites.map((site, index) => {
            const isIncluded = index < fittedCount;
            const isFirstExcluded = index === fittedCount;

            return (
              <React.Fragment key={site.id}>
                {/* Dynamic separator */}
                {isFirstExcluded && (
                  <div className="flex items-center gap-3 py-2">
                    <div className="flex-1 h-px bg-red-500/40" />
                    <span className="text-xs font-bold text-red-400 uppercase tracking-widest shrink-0">
                      ✕ Retirées automatiquement
                    </span>
                    <div className="flex-1 h-px bg-red-500/40" />
                  </div>
                )}

                <div className={cn(
                  "flex items-stretch gap-2 rounded-xl border transition-all",
                  isIncluded
                    ? "bg-zinc-900 border-zinc-700"
                    : "bg-zinc-900/40 border-zinc-800/50 opacity-50"
                )}>
                  {/* Up/Down arrows + rank */}
                  <div className="flex flex-col items-center justify-center px-2 gap-0.5 shrink-0">
                    <button
                      onClick={() => moveUp(index)}
                      disabled={index === 0}
                      className="p-1 text-zinc-500 hover:text-white disabled:opacity-20 transition-colors"
                    >
                      <ChevronUp className="w-4 h-4" />
                    </button>
                    <span className={cn(
                      "text-xs font-bold w-5 text-center",
                      isIncluded ? "text-emerald-400" : "text-zinc-600"
                    )}>
                      {index + 1}
                    </span>
                    <button
                      onClick={() => moveDown(index)}
                      disabled={index === allSites.length - 1}
                      className="p-1 text-zinc-500 hover:text-white disabled:opacity-20 transition-colors"
                    >
                      <ChevronDown className="w-4 h-4" />
                    </button>
                  </div>

                  {/* Thumbnail */}
                  <div className="w-16 h-16 my-2 rounded-lg bg-zinc-800 overflow-hidden shrink-0 border border-white/10 self-center">
                    {site.image && !site.image.includes('maps.googleapis.com') ? (
                      <img src={site.image} alt={site.name} className="w-full h-full object-cover" referrerPolicy="no-referrer" />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center text-xl">📍</div>
                    )}
                  </div>

                  {/* Info */}
                  <div className="flex-1 min-w-0 py-3 pr-1">
                    <h3 className="text-white font-bold text-sm truncate">{site.name}</h3>
                    <p className="text-xs text-zinc-400 line-clamp-2 mt-0.5 leading-snug">{site.description}</p>
                    <div className="flex items-center gap-2 mt-1.5 flex-wrap">
                      {site.durationMinutes && (
                        <span className="flex items-center gap-0.5 text-[10px] text-zinc-500">
                          <Clock className="w-3 h-3" />{site.durationMinutes} min
                        </span>
                      )}
                      {(site.rating || site.googleRating) && (
                        <span className="flex items-center gap-0.5 text-[10px] text-amber-400">
                          <Star className="w-3 h-3 fill-current" />{site.rating || site.googleRating}
                        </span>
                      )}
                      {site.isHandicapAccessible && <Accessibility className="w-3 h-3 text-blue-400" />}
                      {site.isWindingRoad && <Route className="w-3 h-3 text-amber-400" />}
                    </div>
                  </div>

                  {/* Detail button */}
                  <button
                    onClick={() => openDetail(site)}
                    className="px-3 flex items-center justify-center text-zinc-500 hover:text-emerald-400 transition-colors shrink-0 border-l border-zinc-800"
                    title="Voir le détail"
                  >
                    <Info className="w-4 h-4" />
                  </button>
                </div>
              </React.Fragment>
            );
          })}
        </div>
      </div>

      <div className="fixed bottom-0 left-0 right-0 z-30 bg-zinc-950/90 backdrop-blur-xl border-t border-white/10">
        <div className="max-w-7xl mx-auto p-4 md:p-6 flex gap-3">
          <button
            onClick={handleBack}
            className="flex-1 py-3 bg-zinc-800 text-white rounded-xl font-semibold text-sm hover:bg-zinc-700 transition-colors"
          >
            Retour
          </button>
          <button
            onClick={handleNext}
            className="flex-[2] py-3 bg-emerald-500 text-white rounded-xl font-semibold text-sm hover:bg-emerald-600 transition-colors shadow-[0_0_15px_rgba(16,185,129,0.3)]"
          >
            Valider l'ordre
          </button>
        </div>
      </div>
    </div>
  );
};
